import { GraphicPrimitive } from './GraphicPrimitive';

export class Rectangle extends GraphicPrimitive {
  constructor(x, y, width, height, color, fillColor, fillType, spacing, angle, segmentLength) {
    super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'Rectangle');
    this.x1 = x;
    this.y1 = y;
    this.x2 = x + width;
    this.y2 = y + height;
    this.m_isDrawing = false;
    this.m_hasSecondPoint = false;
    this.regenerateStitches();
  }

  toJSON() {
    return {
      ...super.toJSON(),
      x: this.x1,
      y: this.y1,
      width: this.x2 - this.x1,
      height: this.y2 - this.y1
    };
  }

  getControlPoints() {
    return [
      { x: this.x1, y: this.y1 },
      { x: this.x2, y: this.y2 }
    ];
  }

  updateControlPoint(pointIndex, newX, newY) {
    console.log(`Rectangle: Updating control point ${pointIndex} to (${newX}, ${newY})`);
    if (pointIndex === 0) {
      this.x1 = newX;
      this.y1 = newY;
    } else if (pointIndex === 1) {
      this.x2 = newX;
      this.y2 = newY;
    }
    this.regenerateStitches();
  }

  move(dx, dy) {
    this.x1 += dx;
    this.y1 += dy;
    this.x2 += dx;
    this.y2 += dy;
    this.regenerateStitches();
  }

  toPolygon() {
    // Convert rectangle to polygon format for stitch generation
    const minX = Math.min(this.x1, this.x2);
    const maxX = Math.max(this.x1, this.x2);
    const minY = Math.min(this.y1, this.y2);
    const maxY = Math.max(this.y1, this.y2);

    return [[
      [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
        [minX, minY]  // Close the polygon
      ]
    ]];
  }

  regenerateStitches() {
    const multiPoly = this.toPolygon();
    const parallelStitches = [];

    if (this.fillColor && this.fillType === 'parallel') {
      const bounds = {
        minX: Math.min(this.x1, this.x2),
        maxX: Math.max(this.x1, this.x2),
        minY: Math.min(this.y1, this.y2),
        maxY: Math.max(this.y1, this.y2)
      };

      this.generateParallelStitches(bounds, multiPoly);
      if (this.stitchLists.length > 0) {
        this.stitchLists.forEach(list => {
          list.color = this.fillColor;
          parallelStitches.push(list);
        });
      }
    }

    const contourStitches = this.generateContourStitches(multiPoly);
    this.stitchLists = [...parallelStitches, ...contourStitches];
  }

  // Render the rectangle on the canvas
  renderDrawing(ctx, transform, globalStitchIndex, stitchOffset) {
    // Don't render until both points are set
    if (!this.m_hasSecondPoint) return;
    
    const bounds = {
      minX: Math.min(this.x1, this.x2),
      maxX: Math.max(this.x1, this.x2),
      minY: Math.min(this.y1, this.y2),
      maxY: Math.max(this.y1, this.y2)
    };

    this.drawFill(ctx, transform, () => {
      ctx.rect(this.x1, this.y1, this.x2 - this.x1, this.y2 - this.y1);
    }, bounds, globalStitchIndex, stitchOffset);

    // Always draw stitches if they exist
    this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);

    ctx.save();
    ctx.beginPath();
    ctx.rect(this.x1, this.y1, this.x2 - this.x1, this.y2 - this.y1);
    ctx.strokeStyle = this.isSelected ? 'red' : this.color;
    ctx.lineWidth = this.isSelected ? (2 / transform.scale) : (1 / transform.scale);
    ctx.stroke();
    ctx.restore();
  }

  drawPoint(ctx, x, y, color, transform) {
    ctx.beginPath();
    const radius = 5 / transform.scale;
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }

  renderActivePoints(ctx, transform) {
    if (this.x1 && this.y1) {
      this.drawPoint(ctx, this.x1, this.y1, 'red', transform);
    }
    if (this.x2 && this.y2) {
      this.drawPoint(ctx, this.x2, this.y2, 'red', transform);
    }
  }

  /**
   * NEW: Checks if a point is on or near the rectangle's edges.
   * @param {number} px - The x-coordinate of the point to check.
   * @param {number} py - The y-coordinate of the point to check.
   * @param {object} transform - The current canvas transform, used for scaling the tolerance.
   * @returns {boolean} - True if the point is on an edge, false otherwise.
   */
  isPointInside(px, py, transform) {
    // Define a base tolerance in screen pixels. 5px is a good starting point.
    // We divide by the scale to convert screen-space tolerance to world-space.
    const tolerance = 5 / transform.scale;

    const minX = Math.min(this.x1, this.x2);
    const maxX = Math.max(this.x1, this.x2);
    const minY = Math.min(this.y1, this.y2);
    const maxY = Math.max(this.y1, this.y2);

    // Check if the point is near the top or bottom edge
    const onHorizontalEdge = (px >= minX && px <= maxX) &&
      (Math.abs(py - minY) < tolerance || Math.abs(py - maxY) < tolerance);

    // Check if the point is near the left or right edge
    const onVerticalEdge = (py >= minY && py <= maxY) &&
      (Math.abs(px - minX) < tolerance || Math.abs(px - maxX) < tolerance);

    return onHorizontalEdge || onVerticalEdge;
  }

  setIsDrawing(dr) { this.m_isDrawing = dr }

  isDrawing() {
    return this.m_isDrawing
  }

  isComplete() {
    return this.m_hasSecondPoint;
  }

  AddPoint(point) {
    this.x1 = point.x;
    this.y1 = point.y;
    this.m_isDrawing = true;
    this.m_hasSecondPoint = false;
    return false;
  }

  AddSecondPoint(point) {
    // Handle negative drag (bottom-right to top-left) by swapping corners
    const dx = point.x - this.x1;
    const dy = point.y - this.y1;
    
    if (dx < 0) {
      this.x1 = point.x;
      this.x2 = this.x1 + Math.abs(dx);
    } else {
      this.x2 = point.x;
    }
    
    if (dy < 0) {
      this.y1 = point.y;
      this.y2 = this.y1 + Math.abs(dy);
    } else {
      this.y2 = point.y;
    }
    
    this.m_hasSecondPoint = true;
    this.regenerateStitches(); // Generate stitches when shape is completed
    return false;
  }

  static renderPreview(ctx, x1, y1, x2, y2, color, transform) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x1, y1, x2 - x1, y2 - y1);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1 / transform.scale;
    ctx.stroke();
    ctx.restore();
  }

  // Handle mouse events (e.g., resizing or moving the rectangle)
  handleMouseEvent(event) {
    // Placeholder for mouse event handling logic
    console.log('Mouse event:', event);
  }
}